/**
 * `<DslReference>` — what you can type, searchable, click to insert.
 *
 * A DSL with no discoverable vocabulary is only usable by whoever wrote it. The
 * catalog and the column metadata both already exist on the wire; this is the
 * surface that makes them reachable without leaving the editor.
 *
 * Columns are searched by **description as well as name**, which is the whole
 * reason the label dictionary exists: typing "momentum" finds `jforce2` and
 * `kahunaUp`, whose headers read "fM2" and "MomoUp" and which are unfindable by
 * name. A reference that only matched identifiers would help exactly the people
 * who did not need it.
 */

import { useMemo, useState, type CSSProperties } from "react";
import type { ScreenerColumnMeta } from "@qufolio/transport";

import {
  DEFAULT_CATALOG,
  insertionFor,
  screenerCatalog,
  type DslCatalog,
} from "../lib/dsl-catalog.js";

export interface DslReferenceProps {
  catalog?: DslCatalog;
  meta?: readonly ScreenerColumnMeta[];
  /** Insert at the caret. The editor owns the caret, so it owns the insert. */
  onInsert: (text: string) => void;
}

type Tab = "functions" | "columns";

export function DslReference({
  catalog: injectedCatalog = DEFAULT_CATALOG,
  meta = [],
  onInsert,
}: DslReferenceProps) {
  // Narrowed to what a screen can answer: a Symbol Indicator listed here would
  // insert a call that comes back blank. See `screenerCatalog`.
  const catalog = useMemo(() => screenerCatalog(injectedCatalog), [injectedCatalog]);
  const [tab, setTab] = useState<Tab>("functions");
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();

  const functions = useMemo(
    () => catalog.functions.filter((f) => !needle || f.name.includes(needle)),
    [catalog.functions, needle],
  );

  const columns = useMemo(() => {
    if (!needle) return meta.slice(0, 200);
    return meta
      .filter(
        (m) =>
          m.field.toLowerCase().includes(needle) ||
          m.label.toLowerCase().includes(needle) ||
          m.description.toLowerCase().includes(needle),
      )
      .slice(0, 200);
  }, [meta, needle]);

  return (
    <div style={panel}>
      <div style={header}>
        {(["functions", "columns"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              ...tabButton,
              background: tab === t ? "var(--qf-selection)" : "transparent",
              color: tab === t ? "var(--qf-text)" : "var(--qf-text-secondary)",
            }}
          >
            {t === "functions" ? `Functions (${catalog.functions.length})` : `Columns (${meta.length})`}
          </button>
        ))}
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={tab === "columns" ? "name or meaning…" : "search…"}
          aria-label="Search the ScanScript reference"
          style={search}
        />
      </div>

      <div style={list}>
        {tab === "functions"
          ? functions.map((fn) => (
              <button
                key={fn.name}
                onClick={() => onInsert(insertionFor(fn))}
                title={
                  fn.series
                    ? "Reads bar history — computed on the server"
                    : "Computed in the browser from the row"
                }
                style={entry}
              >
                <code style={{ color: "var(--qf-accent)" }}>{fn.name}</code>
                <span style={badge}>{fn.series ? "series" : "scalar"}</span>
              </button>
            ))
          : columns.map((m) => (
              <button
                key={m.field}
                onClick={() => onInsert(m.field)}
                title={m.description || m.field}
                style={entry}
              >
                <code>{m.field}</code>
                {/* The label and timeframe are what make two columns of the same
                    measure distinguishable at a glance. */}
                <span style={{ color: "var(--qf-text-muted)", overflow: "hidden" }}>
                  {m.label || m.description}
                </span>
                {m.timeframe && (
                  <span style={badge}>
                    {m.timeframe.count}
                    {m.timeframe.unit.toUpperCase()}
                  </span>
                )}
              </button>
            ))}

        {(tab === "functions" ? functions : columns).length === 0 && (
          <span style={{ color: "var(--qf-text-muted)", padding: "0.3rem" }}>
            {tab === "columns" && meta.length === 0
              ? "No column metadata yet — connect to a screen."
              : "Nothing matches."}
          </span>
        )}
      </div>

      <div style={footnote}>
        {/* The three facts that are not guessable from a list of names. */}
        Shorthands: {Object.entries(catalog.fieldAliases).map(([k, v]) => `${k}=${v}`).join(", ")}.{" "}
        <code>{catalog.indexField}</code> is the bar number. Frequencies{" "}
        {catalog.frequencyUnits.join(" ")} — <code>mi</code> minute, <code>m</code> month; default{" "}
        <code>{catalog.defaultFrequency}</code>.
      </div>
    </div>
  );
}

const panel: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  background: "var(--qf-background)",
  fontSize: "0.72rem",
};

const header: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.25rem",
  padding: "0.25rem",
  borderBottom: "1px solid var(--qf-border)",
};

const tabButton: CSSProperties = {
  border: "none",
  borderRadius: 3,
  padding: "0.1rem 0.4rem",
  fontSize: "0.68rem",
  cursor: "pointer",
};

const search: CSSProperties = {
  flex: 1,
  minWidth: 60,
  padding: "0.1rem 0.3rem",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 3,
  color: "var(--qf-text)",
  fontSize: "0.7rem",
};

const list: CSSProperties = {
  flex: 1,
  minHeight: "3rem",
  overflowY: "auto",
  padding: "0.15rem",
};

const entry: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
  width: "100%",
  textAlign: "left",
  padding: "0.12rem 0.3rem",
  background: "transparent",
  border: "none",
  borderRadius: 3,
  color: "var(--qf-text)",
  fontSize: "0.72rem",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const badge: CSSProperties = {
  marginLeft: "auto",
  padding: "0 0.25rem",
  borderRadius: 3,
  border: "1px solid var(--qf-border)",
  color: "var(--qf-text-muted)",
  fontSize: "0.62rem",
};

const footnote: CSSProperties = {
  padding: "0.25rem 0.35rem",
  borderTop: "1px solid var(--qf-border)",
  color: "var(--qf-text-muted)",
  fontSize: "0.66rem",
  lineHeight: 1.4,
};
